import styled from '@emotion/styled';
import Users from 'components/Users';
import { useBrandWarnings } from 'hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAdAccountStore, useIntegrationsStore } from 'stores';
import type { BrandListIntegrationStatusProps } from 'types/integrations';
import EditBrandProfileModal from 'ui/Modals/EditBrandProfileModal';
import EditDataIngestionPermissionsModal from 'ui/Modals/EditDataIngestionPermissionsModal';
import SectionLayout from 'ui/SectionLayout';
import BrandOverviewHeader from './Layout/BrandOverviewHeader';
import BrandSection from './Layout/BrandSection';
import useBrandOverview from './useBrandOverview';

const BackLink = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 14px;
  text-decoration: none;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const BrandOverview = () => {
  const navigate = useNavigate();
  const {
    brand,
    billing,
    category,
    isFileUploadAllowed,
    onUpdateBrandCategory,
    onUpsertBrandCustomerSettings,
    apiLoading,
    loading,
    brandCustomerSettingsLoading,
    dateRange,
    audienceSpend,
    audienceSpendSummary,
    audienceStats,
    last30DaysOfAllBrandSpend,
    onAudienceDateRangeSelect,
    masterAdAccounts,
    onConnectAdAccount,
    onDisconnectAdAccount,
    onRefreshBrandBilling
  } = useBrandOverview();
  const { brandWarnings } = useBrandWarnings();
  const { getAllIntegrations } = useIntegrationsStore();
  const { metaAdAccountsList, tiktokAdAccount } = useAdAccountStore();
  const activeIntegrations = getAllIntegrations();

  const [showEditCategoryModal, setShowCategoryModal] = useState(false);
  const [showDataIngestionPermissionsModal, setShowDataIngestionPermissionsModal] = useState(false);

  /*
    example: integrationsWithStatus = { facebook: [integration1, integration2], shopify: [integration3] }
  */
  const integrationsWithStatus = activeIntegrations.reduce(
    (acc, integration) => {
      if (!acc[integration.type]) acc[integration.type] = [];
      acc[integration.type].push(integration);
      return acc;
    },
    {} as BrandListIntegrationStatusProps['integrations']
  );

  return (
    <SectionLayout
      title="Overview"
      titleAppendText={
        <>
          <BackLink onClick={() => navigate('/brands')}>[← Back to Brands]</BackLink>
          <BrandOverviewHeader
            brand={brand}
            metaAdAccountId={metaAdAccountsList.at(0)?.adAccountId}
            tiktokAdAccountId={tiktokAdAccount?.accountId}
            integrations={integrationsWithStatus}
          />
        </>
      }
    >
      <BrandSection
        loading={loading}
        apiLoading={apiLoading}
        brand={brand}
        masterAdAccounts={masterAdAccounts}
        onConnectAdAccount={onConnectAdAccount}
        onDisconnectAdAccount={onDisconnectAdAccount}
        brandWarnings={brandWarnings}
        brandCustomerSettingsLoading={brandCustomerSettingsLoading}
        isFileUploadAllowed={isFileUploadAllowed}
        last30DaysOfAllBrandSpend={last30DaysOfAllBrandSpend}
        billing={billing}
        category={category}
        dateRange={dateRange}
        audienceSpend={audienceSpend}
        audienceSpendSummary={audienceSpendSummary}
        audienceStats={audienceStats}
        onAudienceDateRangeSelect={onAudienceDateRangeSelect}
        onRefreshBrandBilling={onRefreshBrandBilling}
        onEditBrandCategory={() => setShowCategoryModal(true)}
        onEditDataIngestionPermissions={() => setShowDataIngestionPermissionsModal(true)}
      />

      <Users />

      {/* ==== modals ==== */}
      <EditBrandProfileModal
        open={showEditCategoryModal}
        currentCategory={category}
        onClose={() => setShowCategoryModal(false)}
        onUpdateBrandCategory={onUpdateBrandCategory}
      />
      <EditDataIngestionPermissionsModal
        isFileUploadAllowed={isFileUploadAllowed}
        open={showDataIngestionPermissionsModal}
        onClose={() => setShowDataIngestionPermissionsModal(false)}
        onUpsertBrandCustomerSettings={onUpsertBrandCustomerSettings}
      />
    </SectionLayout>
  );
};

export default BrandOverview;
